/**
 * ========================
 * RIDES DOMAIN MENU PROVIDER
 * ========================
 *
 * Menu configuration for the Carpool Management (verified church carpooling) admin console.
 * Sections:
 *  1. Overview: Dashboard
 *  2. Operations: Drivers, Verification queue, Documents (uploaded copies), Bookings, Listings, Disputes
 *  3. Security: Expected drivers
 *  4. Configuration: Campuses, Events
 *  5. Administration: Settings, Copy & wording, Lookups
 *
 * Visibility comes from RIDES_PAGE_ACCESS in useRidesPermissions, the same predicates the
 * routes hand to RidesGate, so an item shows exactly when its page would open. A section
 * shows when any of its items does.
 */

import {
  IconDashboard,
  IconSteeringWheel,
  IconShieldCheck,
  IconTicket,
  IconDoorEnter,
  IconFlag,
  IconUserCheck,
  IconBuildingChurch,
  IconCalendarEvent,
  IconSettings,
  IconDatabase,
  IconTypography,
  IconFiles,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection } from '../../layouts/menu/types';
import { RIDES_PAGE_ACCESS as page, ridesMenuVisibility as when } from '../../hooks/rides/useRidesPermissions';

class RidesMenuProvider implements DomainMenuProvider {
  domainId = 'rides';

  getMenuConfig() {
    const sections: MenuSection[] = [
      {
        id: 'rides-overview',
        label: 'Overview',
        visible: when(page.dashboard),
        items: [{ id: 'rides-dashboard', label: 'Dashboard', path: '/app/rides', icon: <IconDashboard size={16} />, visible: when(page.dashboard) }],
      },
      {
        id: 'rides-operations',
        label: 'Operations',
        visible: when(page.drivers, page.verifications, page.documents, page.bookings, page.listings, page.disputes),
        items: [
          { id: 'rides-drivers', label: 'Drivers', path: '/app/rides/drivers', icon: <IconSteeringWheel size={16} />, visible: when(page.drivers) },
          { id: 'rides-verifications', label: 'Verification queue', path: '/app/rides/verifications', icon: <IconUserCheck size={16} />, visible: when(page.verifications) },
          { id: 'rides-documents', label: 'Documents', path: '/app/rides/documents', icon: <IconFiles size={16} />, visible: when(page.documents) },
          { id: 'rides-bookings', label: 'Bookings', path: '/app/rides/bookings', icon: <IconTicket size={16} />, visible: when(page.bookings) },
          { id: 'rides-listings', label: 'Listings', path: '/app/rides/listings', icon: <IconDoorEnter size={16} />, visible: when(page.listings) },
          { id: 'rides-disputes', label: 'Disputes', path: '/app/rides/disputes', icon: <IconFlag size={16} />, visible: when(page.disputes) },
        ],
      },
      {
        id: 'rides-security',
        label: 'Security',
        visible: when(page.security),
        items: [{ id: 'rides-security-desk', label: 'Expected drivers', path: '/app/rides/security', icon: <IconShieldCheck size={16} />, visible: when(page.security) }],
      },
      {
        id: 'rides-configuration',
        label: 'Configuration',
        visible: when(page.campuses, page.events),
        items: [
          { id: 'rides-campuses', label: 'Campuses', path: '/app/rides/campuses', icon: <IconBuildingChurch size={16} />, visible: when(page.campuses) },
          { id: 'rides-events', label: 'Events', path: '/app/rides/events', icon: <IconCalendarEvent size={16} />, visible: when(page.events) },
        ],
      },
      {
        id: 'rides-administration',
        label: 'Administration',
        visible: when(page.settings, page.copy, page.lookups),
        items: [
          { id: 'rides-settings', label: 'Settings', path: '/app/rides/admin/settings', icon: <IconSettings size={16} />, visible: when(page.settings) },
          { id: 'rides-copy', label: 'Copy & wording', path: '/app/rides/admin/copy', icon: <IconTypography size={16} />, visible: when(page.copy) },
          { id: 'rides-lookups', label: 'Lookups', path: '/app/rides/admin/lookups', icon: <IconDatabase size={16} />, visible: when(page.lookups) },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: {
        version: '1.0.0',
        lastUpdated: Date.now(),
        description: 'Carpool management menu',
      },
    };
  }
}

export const ridesMenuProvider = new RidesMenuProvider();
